import * as React from 'react';
import type { FieldValues, FieldPath, UseControllerProps, UseFormUnregister } from 'react-hook-form';

import { TextAreaCell } from '../../RHFTableCellComponents/TextArea/TextAreaCell';
import type { RenderMethodParams } from '../RHFFieldCellWrapper';
import RHFFieldWrapper from '../RHFFieldCellWrapper';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues> =
	UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		rhfId: number | string;
		label?: string;
		placeholder?: string;
		onChange: (name: string) => void;
		onBlur: () => void;
		unregister: UseFormUnregister<TFieldValues>;
		fieldsLength: number;
	};

function TextAreaField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, onChange, onBlur, ...rest } = props;

	const renderTextAreaCell = React.useCallback((
		{
			field,
			error,
			onFieldChange,
			onFieldBlur,
		}: RenderMethodParams<TFieldValues, TName>
	) => (
		<TextAreaCell
			elementKey={`${rhfId}`}
			error={error ?? undefined}
			key={name + rhfId}
			name={field.name}
			onBlur={onFieldBlur}
			onChange={onFieldChange}
			placeholder={placeholder}
			value={field.value}
		/>
	), [name, rhfId, placeholder]);

	return (
		<RHFFieldWrapper
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			{...rest}
			render={renderTextAreaCell}
		/>
	);
}

export default React.memo(TextAreaField) as typeof TextAreaField;
